import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, Input, Spinner, Text, VStack } from '@minetrace/ui'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { LuCircleHelp, LuSearch, LuWifiOff } from 'react-icons/lu'
import { motion } from 'framer-motion'
import { CertificadoResultado } from './CertificadoResultado'
import { IndiceResultado } from './IndiceResultado'
import { resolverCodigoPublico, type ResolucionCodigo } from './verificacionPublica'

const MotionBox = motion.create(Box)

/**
 * LA PÁGINA PÚBLICA DE VERIFICACIÓN. Sin sesión, sin organización y sin menú.
 *
 * <p>ESTÁ FUERA DEL CASCARÓN AUTENTICADO A PROPÓSITO: cuelga de la raíz del router,
 * al lado de `/login` y `/politica-privacidad`, y NO de la rama `/app` que va envuelta
 * en `ProtectedRoute` + `AppLayout`. Si verificar exigiera una credencial de la propia
 * entidad que emitió el documento, la verificación no acreditaría nada ante un tercero,
 * que es justamente para lo que existe. Por el mismo motivo esta página no llama al
 * `apiClient` (ver `verificacionPublica.ts`).</p>
 *
 * <p>UNA SOLA PUERTA PARA LOS DOS CÓDIGOS. El backend imprime la MISMA URL
 * `/verificar/{código}` en el QR de la carátula del expediente (CUID) y en el del
 * Índice Digital (CVD) —ver `PublicWebUrlResolver.VerificationUrl`—, precisamente
 * porque quien tiene un papel delante no sabe cuál de los dos está leyendo. La ruta
 * resuelve el código que le llegue y enseña el resultado que corresponda.</p>
 *
 * <p>SIN COMPROBACIÓN DE FORMATO EN EL CLIENTE. Se manda lo que la persona teclee y
 * el servidor canonicaliza (`CvdGenerator.Normalize` ya pliega I/L→1 y O→0 y se come
 * guiones y espacios). Validar aquí reintroduciría en el navegador el oráculo de
 * formato que el backend evita devolviendo un 404 idéntico para el código inexistente
 * y para el mal formado.</p>
 */
export default function VerificarPage() {
  const { code } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState(code ?? '')

  // El landing público (kuntur.com.pe) verifica sin JS con un <form method="get">,
  // y un GET sólo puede producir `?code=`, no un segmento de ruta. Se canonicaliza
  // a /verificar/{código} para que la URL compartible sea siempre la misma.
  const queryCode = searchParams.get('code')
  useEffect(() => {
    if (!code && queryCode?.trim()) {
      navigate(`/verificar/${encodeURIComponent(queryCode.trim())}`, { replace: true })
    }
  }, [code, queryCode, navigate])
  const [resultado, setResultado] = useState<ResolucionCodigo | null>(null)
  const [loading, setLoading] = useState(false)

  const verify = useCallback(async (typed: string) => {
    if (!typed.trim()) return
    setLoading(true)
    setResultado(null)
    try {
      setResultado(await resolverCodigoPublico(typed.trim()))
    } catch {
      // `resolverCodigoPublico` ya absorbe los fallos de cada extremo; esto sólo
      // cubre lo imprevisto, y también acaba en "no se ha podido comprobar" y nunca
      // en "ese código no existe".
      setResultado({ kind: 'sinConexion' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (code) void verify(code)
  }, [code, verify])

  const handleSearch = () => {
    const typed = searchInput.trim()
    if (!typed) return
    // Si el código de la URL ya es éste, el efecto no se dispara: se verifica a mano.
    if (typed === code) void verify(typed)
    else navigate(`/verificar/${encodeURIComponent(typed)}`, { replace: true })
  }

  return (
    <Box minH="100vh" bg="#f7faf8">
      {/* Header */}
      <Box bg="#012d1d" px={8} py={6}>
        <Box maxW="800px" mx="auto">
          <Text fontSize="xl" fontWeight="800" color="white" fontFamily="heading">KUNTUR</Text>
          <Text fontSize="sm" color="#a5d0b9">Compliance & Debida Diligencia</Text>
        </Box>
      </Box>

      {/* Content */}
      <Box maxW="800px" mx="auto" px={6} py={10}>
        <VStack gap={8} alignItems="stretch">
          {/* Title */}
          <Box textAlign="center">
            <Text fontSize="2xl" fontWeight="800" color="#012d1d" fontFamily="heading">
              Verificación de documentos
            </Text>
            <Text color="#414844" mt={2}>
              Escanee el código QR del documento o escriba aquí el código impreso: el Código de
              Verificación Digital de un Índice Digital, o el código CUID de un certificado de
              minero.
            </Text>
          </Box>

          {/* Search */}
          <Flex gap={2} maxW="500px" mx="auto" w="100%" alignItems="flex-start">
            <Box flex={1}>
              <Input
                value={searchInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value.slice(0, 50))}
                placeholder="Ej: A1B2-C3D4-E5F6"
                fontFamily="monospace"
                maxLength={50}
                w="100%"
                aria-label="Código de verificación"
                onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleSearch()}
              />
              <Text fontSize="xs" color="#857b71" mt={1} textAlign="right" fontVariantNumeric="tabular-nums">
                {searchInput.length}/50
              </Text>
            </Box>
            <Box
              as="button" type="button" display="flex" alignItems="center" justifyContent="center"
              px={5} bg="#012d1d" color="white" borderRadius="sm" cursor="pointer"
              flexShrink={0} _hover={{ opacity: 0.9 }}
              aria-label="Verificar"
              onClick={handleSearch}
            >
              {loading ? <Spinner size="sm" color="white" /> : <LuSearch size={18} />}
            </Box>
          </Flex>

          {/* Loading */}
          {loading && (
            <Box textAlign="center" py={8} role="status">
              <Spinner size="lg" color="#012d1d" />
              <Text fontSize="sm" color="#857b71" mt={3}>Comprobando el código...</Text>
            </Box>
          )}

          {/* Resultado */}
          {!loading && resultado && (
            <MotionBox
              key={resultado.kind}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              {resultado.kind === 'indice' && (
                <IndiceResultado data={resultado.data} onReintentar={() => void verify(searchInput || code || '')} />
              )}
              {resultado.kind === 'certificado' && <CertificadoResultado data={resultado.data} />}
              {resultado.kind === 'noEncontrado' && <NoEncontrado />}
              {resultado.kind === 'sinConexion' && (
                <SinConexion onReintentar={() => void verify(searchInput || code || '')} />
              )}
            </MotionBox>
          )}

          {/* Initial state */}
          {!loading && !resultado && (
            <Box p={8} textAlign="center">
              <Text fontSize="sm" color="#857b71">
                Puede escribir el código con o sin guiones y en minúsculas: lo entendemos igual.
              </Text>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  )
}

/**
 * UN SOLO ESTADO PARA "NO CONSTA", igual que el backend devuelve un solo 404 para el
 * código inexistente y para el mal formado. No se le dice a nadie que "el formato es
 * incorrecto": esa distinción sería un oráculo para quien enumere y no ayuda a quien
 * se equivocó al copiar.
 *
 * NO ES UNA ACUSACIÓN Y NO SE PINTA COMO TAL. Que un código no conste casi siempre
 * significa que se copió mal, así que el tono es neutro y el texto lo dice.
 */
function NoEncontrado() {
  return (
    <Box p={8} bg="white" borderRadius="xl" border="1px solid #e0e3e1" textAlign="center">
      <Flex justifyContent="center" mb={3} color="#857b71" aria-hidden="true">
        <LuCircleHelp size={28} />
      </Flex>
      <Text fontSize="lg" fontWeight="700" color="#2a2622">
        No encontramos ese código
      </Text>
      <Text fontSize="sm" color="#414844" mt={2} lineHeight="1.7">
        Ningún documento de esta plataforma consta con el código que ha escrito. Lo más habitual
        es un error al copiarlo: compruebe los caracteres uno a uno con el papel que tiene
        delante. Los guiones, los espacios y las mayúsculas no importan.
      </Text>
    </Box>
  )
}

/**
 * NO SE HA PODIDO PREGUNTAR. Distinto de "no consta" y, sobre todo, distinto de "no es
 * auténtico": no se ha comprobado nada.
 */
function SinConexion({ onReintentar }: { onReintentar: () => void }) {
  return (
    <Box p={8} bg="white" borderRadius="xl" border="1px solid #e0e3e1" textAlign="center">
      <Flex justifyContent="center" mb={3} color="#857b71" aria-hidden="true">
        <LuWifiOff size={28} />
      </Flex>
      <Text fontSize="lg" fontWeight="700" color="#2a2622">
        No hemos podido comprobar el código
      </Text>
      <Text fontSize="sm" color="#414844" mt={2} lineHeight="1.7">
        No se ha podido contactar con el servicio de verificación. Esto no dice nada sobre el
        documento: no se ha llegado a comprobar. Revise su conexión y vuelva a intentarlo.
      </Text>
      <Box
        as="button" type="button" display="inline-flex" alignItems="center" justifyContent="center"
        mt={4} px={4} py={2} borderRadius="sm" bg="#b4532a" color="white"
        fontSize="sm" fontWeight="600" cursor="pointer" _hover={{ opacity: 0.9 }}
        onClick={onReintentar}
      >
        Volver a intentarlo
      </Box>
    </Box>
  )
}
